/**
 * CameraCapture — burst camera used by Doserne Progresjon.
 *
 * Opens a full-screen live camera preview (rear camera by default) and
 * lets the worker snap photos one after another WITHOUT closing the
 * stream between shots. Each snap is added to a thumbnail strip at the
 * bottom; when they're done they tap "Ferdig" and every photo is
 * handed back to the parent as `File[]` (JPEG, quality 0.85).
 *
 * Props:
 *   - onDone(files: File[])  — user finished; parent stages the photos
 *   - onCancel()             — user backed out; nothing captured
 *   - maxPhotos (default 20) — safety cap so we don't blow memory
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, Check, RotateCcw, RefreshCw, Loader2 } from "lucide-react";

export default function CameraCapture({ onDone, onCancel, maxPhotos = 20 }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState("environment"); // "environment" (rear) | "user" (front)
  const [busy, setBusy] = useState(false);
  const [shots, setShots] = useState([]); // [{ file, previewUrl }]

  const start = useCallback(async (mode) => {
    setError(null);
    setReady(false);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode || facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => { /* autoplay policy */ });
      }
      setReady(true);
    } catch (e) {
      // Common: NotAllowedError (permission denied), NotFoundError (no camera)
      setError(e?.name || "Camera error");
    }
  }, [facing]);

  useEffect(() => {
    start(facing);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      // Also revoke object URLs on unmount so we don't leak memory
      setShots((cur) => { cur.forEach((s) => URL.revokeObjectURL(s.previewUrl)); return cur; });
    };
  }, []);

  const snap = useCallback(async () => {
    if (!ready || busy) return;
    if (shots.length >= maxPhotos) return;
    setBusy(true);
    try {
      const v = videoRef.current;
      const c = canvasRef.current;
      const w = v.videoWidth || 1280;
      const h = v.videoHeight || 720;
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, w, h);
      const blob = await new Promise((resolve) => c.toBlob(resolve, "image/jpeg", 0.85));
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        const previewUrl = URL.createObjectURL(blob);
        setShots((cur) => [...cur, { file, previewUrl }]);
      }
    } finally {
      setBusy(false);
    }
  }, [ready, busy, shots.length, maxPhotos]);

  const remove = (i) => setShots((cur) => {
    URL.revokeObjectURL(cur[i].previewUrl);
    return cur.filter((_, idx) => idx !== i);
  });

  const flip = () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    start(next);
  };

  const finish = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    onDone(shots.map((s) => s.file));
  };

  const cancel = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    shots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="camera-capture">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 text-white">
        <button
          onClick={cancel}
          className="w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center"
          data-testid="camera-cancel"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-xs font-black uppercase tracking-widest opacity-80">
          {shots.length}/{maxPhotos} bilder
        </div>
        <button
          onClick={flip}
          className="w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center"
          title="Bytt kamera"
          data-testid="camera-flip"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Live preview */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {error && (
          <div className="text-center px-6 text-white">
            <Camera className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <div className="font-black">Kunne ikke åpne kamera</div>
            <div className="text-xs opacity-70 mt-1">Feilkode: {error}. Sjekk at nettleseren har tilgang til kameraet.</div>
            <button onClick={() => start(facing)} className="mt-4 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm">
              <RefreshCw className="w-4 h-4" /> Prøv igjen
            </button>
          </div>
        )}
        {!error && !ready && <Loader2 className="w-10 h-10 animate-spin text-white/70" />}
        <video
          ref={videoRef}
          playsInline
          muted
          className={`w-full h-full object-contain ${ready ? "" : "opacity-0"}`}
          data-testid="camera-video"
        />
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Thumbnail strip */}
      {shots.length > 0 && (
        <div className="bg-black/60 px-2 py-2 overflow-x-auto">
          <div className="flex gap-2">
            {shots.map((s, i) => (
              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-white/20">
                <img src={s.previewUrl} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => remove(i)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center"
                  style={{ color: "#fff" }}
                  data-testid={`camera-remove-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div className="px-6 pb-8 pt-4 bg-black/60 flex items-center justify-between gap-4">
        <button
          onClick={cancel}
          className="text-sm font-black text-white/80 hover:text-white"
          data-testid="camera-cancel-bottom"
        >
          Avbryt
        </button>
        <button
          onClick={snap}
          disabled={!ready || busy || shots.length >= maxPhotos}
          className="w-20 h-20 rounded-full bg-white active:scale-95 disabled:opacity-40 flex items-center justify-center shadow-2xl ring-4 ring-white/20 transition"
          data-testid="camera-shutter"
        >
          <div className="w-16 h-16 rounded-full border-4 border-black" />
        </button>
        <button
          onClick={finish}
          disabled={shots.length === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-sm font-black"
          style={{ color: "#fff" }}
          data-testid="camera-done"
        >
          <Check className="w-4 h-4" /> Ferdig
        </button>
      </div>
    </div>
  );
}
